import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export default function AdminAttributes() {
  const [attrs, setAttrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAttr, setNewAttr] = useState({ name: "", display_name: "", input_type: "select", applicable_departments: [] });
  const [valueInputs, setValueInputs] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/attributes");
      setAttrs(data);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createAttr = async () => {
    if (!newAttr.name || !newAttr.display_name) return toast.error("Name + display name required");
    try {
      await api.post("/admin/attributes", newAttr);
      setNewAttr({ name: "", display_name: "", input_type: "select", applicable_departments: [] });
      toast.success("Attribute created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteAttr = async (id) => {
    if (!window.confirm("Delete this attribute and all its values?")) return;
    await api.delete(`/admin/attributes/${id}`);
    toast.success("Deleted");
    load();
  };

  const addValue = async (attr) => {
    const input = valueInputs[attr.id] || {};
    if (!input.display_value) return toast.error("Display value required");
    try {
      await api.post(`/admin/attributes/${attr.id}/values`, {
        value: input.display_value.toLowerCase(),
        display_value: input.display_value,
        hex_color: input.hex_color || null,
      });
      setValueInputs({ ...valueInputs, [attr.id]: { display_value: "", hex_color: "" } });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteValue = async (valueId) => {
    if (!window.confirm("Delete this value?")) return;
    await api.delete(`/admin/attributes/values/${valueId}`);
    load();
  };

  const toggleDept = (dept) =>
    setNewAttr((a) => {
      const cur = a.applicable_departments || [];
      return { ...a, applicable_departments: cur.includes(dept) ? cur.filter((d) => d !== dept) : [...cur, dept] };
    });

  return (
    <div data-testid="admin-attributes-page">
      <div className="overline mb-2">Catalog Master</div>
      <h1 className="font-display text-4xl mb-8">Attributes & Values</h1>

      <section className="bg-white border border-[hsl(var(--kb-border))] p-6 mb-8">
        <div className="overline mb-4">Create New Attribute</div>
        <div className="grid md:grid-cols-4 gap-3">
          <input className="kb-input" placeholder="Name (e.g. fabric)" value={newAttr.name} onChange={(e) => setNewAttr({ ...newAttr, name: e.target.value.replace(/[^a-z0-9_]/g, "") })} data-testid="new-attr-name" />
          <input className="kb-input" placeholder="Display Name" value={newAttr.display_name} onChange={(e) => setNewAttr({ ...newAttr, display_name: e.target.value })} data-testid="new-attr-display" />
          <select className="kb-input" value={newAttr.input_type} onChange={(e) => setNewAttr({ ...newAttr, input_type: e.target.value })} data-testid="new-attr-type">
            <option value="select">Select</option>
            <option value="multiselect">Multiselect</option>
            <option value="swatch">Swatch (Color)</option>
            <option value="toggle">Toggle</option>
            <option value="range">Range</option>
          </select>
          <button className="kb-btn-primary" onClick={createAttr} data-testid="create-attr-btn">Add</button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {["women", "men", "kids", "accessories"].map((d) => (
            <button key={d} onClick={() => toggleDept(d)} className={`chip ${newAttr.applicable_departments?.includes(d) ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`}>
              {d}
            </button>
          ))}
          <span className="text-[hsl(var(--kb-muted))] ml-2 self-center">Applicable departments</span>
        </div>
      </section>

      {loading ? (
        <div>Loading…</div>
      ) : attrs.length === 0 ? (
        <div className="text-[hsl(var(--kb-muted))]">No attributes defined.</div>
      ) : (
        <div className="space-y-4">
          {attrs.map((a) => {
            const input = valueInputs[a.id] || { display_value: "", hex_color: "" };
            return (
              <div key={a.id} className="bg-white border border-[hsl(var(--kb-border))] p-5" data-testid={`attr-${a.slug}`}>
                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="font-display text-xl">{a.display_name}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))]">
                      slug: <b>{a.slug}</b> · type: {a.input_type} · departments: {(a.applicable_departments || []).join(", ") || "all"}
                    </div>
                  </div>
                  <button onClick={() => deleteAttr(a.id)} className="text-[hsl(var(--kb-muted))] hover:text-red-700" aria-label="Delete" data-testid={`delete-attr-${a.slug}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(a.values || []).map((v) => (
                    <span
                      key={v.id}
                      className="inline-flex items-center gap-2 px-3 py-1 border border-[hsl(var(--kb-border))] text-xs"
                    >
                      {v.hex_color && <span className="w-3 h-3 rounded-full border" style={{ background: v.hex_color }} />}
                      {v.display_value}
                      <button onClick={() => deleteValue(v.id)} className="text-[hsl(var(--kb-muted))] hover:text-red-700" aria-label="Remove">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input className="kb-input" placeholder="New value" value={input.display_value} onChange={(e) => setValueInputs({ ...valueInputs, [a.id]: { ...input, display_value: e.target.value } })} data-testid={`val-input-${a.slug}`} />
                  {(a.input_type === "swatch" || a.slug === "color") && (
                    <input type="color" className="w-12 h-12 border border-[hsl(var(--kb-border))]" value={input.hex_color || "#000000"} onChange={(e) => setValueInputs({ ...valueInputs, [a.id]: { ...input, hex_color: e.target.value } })} />
                  )}
                  <button className="kb-btn-primary !py-2 !px-4" onClick={() => addValue(a)} data-testid={`add-val-${a.slug}`}>
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
